import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@ridereserv/shared/utils";

type AlertVariant = "default" | "destructive" | "success" | "warning";

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
}

/**
 * Alert component for displaying important messages.
 * Uses semantic color tokens from the design system.
 */
const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = "default", ...props }, ref) => {
    const variants: Record<AlertVariant, string> = {
      default: "bg-neutral-50 border-neutral-200 text-neutral-800",
      destructive: "bg-destructive-50 border-destructive-100 text-destructive-700",
      success: "bg-success-50 border-success-100 text-success-700",
      warning: "bg-warning-50 border-warning-100 text-warning-700",
    };

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          "relative w-full rounded-md border p-4",
          variants[variant],
          className
        )}
        {...props}
      />
    );
  }
);
Alert.displayName = "Alert";

const AlertTitle = forwardRef<
  HTMLHeadingElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
